import React, { useState, useEffect, useRef } from 'react';
import { get, post } from '../../helpers/apiHelper';
import CustomDatePicker from '../general/CustomDatePicker';
import './EmbeddedForm.css';

const countryOptions = [
    { value: 'España', label: 'España' },
    { value: 'Chile', label: 'Chile' },
    { value: 'Perú', label: 'Perú' },
    { value: 'Otro', label: 'Otro' },
];

const telefonoPrefixOptions = [
    { value: '+34', label: '+34' },
    { value: '+56', label: '+56' },
    { value: '+51', label: '+51' },
];

const generoOptions = [
    { value: 'HOMBRE', label: 'Hombre' },
    { value: 'MUJER', label: 'Mujer' },
    { value: 'NO_BINARIO', label: 'No binario' },
    { value: 'PREFIERO_NO_DECIRLO', label: 'Prefiero no decirlo' },
];

const EmbeddedRegistrationForm = ({
    tiendaId,
    embedCode,
    customStyles = {},
    theme = 'default',
    onSuccess,
    onError,
    showHeader = true,
    showBenefits = true,
    className = '',
    code = null, // Código corto opcional para registro con código (más seguro que token JWT)
    marcaId = null, // ID de marca cuando se usa con code
    tiendaData = null // Datos de tienda cuando se usa con code
}) => {
    const [tienda, setTienda] = useState(null);
    const [tiendas, setTiendas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        nombre: '',
        apellido: '',
        email: '',
        telefonoPrefix: '+34',
        telefonoNumber: '',
        fechaCumpleanos: '',
        genero: 'PREFIERO_NO_DECIRLO',
        geographic_info: { country: '', state: '', city: '' },
        tiendaHabitual: '', // Nueva campo para tienda de consumo habitual
    });
    const [codigoReferido, setCodigoReferido] = useState('');
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Estados para Challenge Token (método recomendado)
    const challengeFrameRef = useRef(null);
    const [challengeToken, setChallengeToken] = useState(null);
    
    // Estado para honeypot (OBLIGATORIO)
    const [honeypot, setHoneypot] = useState('');
    
    // URL del challenge (desde api.petitprime.com)
    const challengeUrl = process.env.REACT_APP_BASE_API_URL 
        ? `${process.env.REACT_APP_BASE_API_URL}/clientes/embed/challenge`
        : 'https://api.petitprime.com/api/clientes/embed/challenge';

    // Estilos por tema
    const themes = {
        default: {
            primaryColor: '#3B82F6',
            backgroundColor: '#FFFFFF',
            textColor: '#1F2937',
            borderColor: '#D1D5DB',
            borderRadius: '8px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        },
        minimal: {
            primaryColor: '#000000',
            backgroundColor: '#FFFFFF',
            textColor: '#000000',
            borderColor: '#E5E7EB',
            borderRadius: '4px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        },
        modern: {
            primaryColor: '#8B5CF6',
            backgroundColor: '#F8FAFC',
            textColor: '#1E293B',
            borderColor: '#CBD5E1',
            borderRadius: '12px',
            fontFamily: 'Inter, system-ui, sans-serif'
        }
    };

    const currentTheme = { ...themes[theme], ...customStyles };

    // Escuchar mensajes del iframe del challenge
    useEffect(() => {
        const handleMessage = (event) => {
            // Validar origen (solo aceptar mensajes de petitprime.com)
            const trustedOrigin = process.env.REACT_APP_BASE_API_URL 
                ? new URL(process.env.REACT_APP_BASE_API_URL).origin
                : 'https://api.petitprime.com';
            
            if (event.origin !== trustedOrigin) {
                return;
            }

            const { type, challengeToken: token, error } = event.data;

            if (type === 'challenge:success') {
                setChallengeToken(token);
                console.log('✅ Challenge token obtenido');
                // Cambiar color del borde del iframe para indicar éxito
                if (challengeFrameRef.current) {
                    challengeFrameRef.current.style.borderColor = '#28a745';
                }
            } else if (type === 'challenge:error') {
                console.error('❌ Error en challenge:', error);
                setChallengeToken(null);
                if (challengeFrameRef.current) {
                    challengeFrameRef.current.style.borderColor = '#dc3545';
                }
            } else if (type === 'challenge:expired') {
                console.warn('⚠️ Challenge expirado');
                setChallengeToken(null);
            } else if (type === 'resize' && challengeFrameRef.current) {
                // Auto-resize del iframe
                challengeFrameRef.current.style.height = `${event.data.height}px`;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    useEffect(() => {
        async function fetchData() {
            try {
                // Si tenemos code (código de embed), significa que ya tenemos la configuración
                // Usar los datos pasados directamente
                if (code) {
                    // Asegurarnos de tener al menos marcaId y tiendaId
                    if (marcaId && (tiendaId || (tiendaData && tiendaData.id))) {
                        const finalTiendaId = tiendaId || (tiendaData && tiendaData.id);

                        // Construir objeto tienda con la información disponible
                        const tiendaObj = {
                            _id: finalTiendaId,
                            id: finalTiendaId,
                            nombre: (tiendaData && tiendaData.nombre) || '',
                            marca: marcaId,
                            poblacion: (tiendaData && tiendaData.poblacion) || ''
                        };

                        setTienda(tiendaObj);
                        setTiendas([tiendaObj]);
                    }
                    setLoading(false);
                    return;
                }

                if (embedCode) {
                    // Formulario global - obtener configuración y tiendas
                    const [configData, tiendasData] = await Promise.all([
                        get(`embed/public/${embedCode}`),
                        get('tiendas/public')
                    ]);
                    setTienda(configData.tienda || null);
                    setTiendas(tiendasData || []);
                } else if (tiendaId) {
                    // Formulario específico de tienda
                    const data = await get(`tiendas/public/${tiendaId}`);
                    setTienda(data);
                    setTiendas([data]);
                } else {
                    // No hay datos para cargar
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                if (onError) onError(error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [tiendaId, embedCode, code, onError, marcaId, tiendaData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('geo_')) {
            const key = name.split('_')[1];
            setFormData((prev) => ({
                ...prev,
                geographic_info: { ...prev.geographic_info, [key]: value },
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    // const handleCodigoReferidoChange = (e) => {
    //     setCodigoReferido(e.target.value);
    // };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.nombre.trim()) newErrors.nombre = "El nombre es obligatorio.";
        if (!formData.apellido.trim()) newErrors.apellido = "El apellido es obligatorio.";
        if (!formData.email.trim()) newErrors.email = "El correo electrónico es obligatorio.";
        // Teléfono es opcional, no validar
        if (!formData.fechaCumpleanos) newErrors.fechaCumpleanos = "La fecha de nacimiento es obligatoria.";
        if (!formData.genero) newErrors.genero = "El género es obligatorio.";
        if (!formData.tiendaHabitual) newErrors.tiendaHabitual = "Debes seleccionar tu tienda de consumo habitual.";
        if (!acceptTerms) newErrors.acceptTerms = "Debes aceptar los términos y condiciones.";

        const { country, state, city } = formData.geographic_info;
        if ((state || city) && !country) {
            newErrors.geographic_info = "Si ingresas estado o ciudad, debes seleccionar un país.";
        }
        if (city && !state) {
            newErrors.geographic_info = "Si ingresas ciudad, debes ingresar también el estado o región.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        // Validar honeypot (OBLIGATORIO)
        if (honeypot === undefined || honeypot === null) {
            console.error('ERROR: Campo honeypot (website) es obligatorio');
            if (onError) onError(new Error('Error de configuración del formulario. Contacta al administrador.'));
            return;
        }
        
        if (honeypot && honeypot.trim() !== '') {
            console.warn('🚫 Bot detectado por honeypot');
            if (onError) onError(new Error('Bot detectado'));
            return;
        }
        
        if (!validateForm()) {
            if (onError) onError(new Error('Por favor, corrige los errores en el formulario.'));
            return;
        }

        // Validar Challenge Token (OBLIGATORIO)
        if (!challengeToken) {
            alert('Por favor, completa la verificación de seguridad primero. El challenge token es obligatorio para registrar clientes.');
            return;
        }

        setIsSubmitting(true);
        // Teléfono es opcional, solo incluirlo si tiene valor
        const telefonoCompleto = formData.telefonoNumber.trim()
            ? formData.telefonoPrefix + formData.telefonoNumber
            : '';
        const { telefonoPrefix, telefonoNumber, tiendaHabitual, ...rest } = formData;
        const payload = {
            ...rest,
            telefono: telefonoCompleto || undefined, // Solo incluir si tiene valor
            geographic_info: { ...rest.geographic_info, postal_code: '' },
            marca: tienda.marca,
            tienda: tiendaHabitual, // Usar la tienda seleccionada por el usuario
            acceptTerms: true, // Siempre true cuando se envía el formulario
            acceptPrivacy: true,
            acceptMarketing: false,
            // Campos de seguridad (OBLIGATORIOS)
            website: '', // Honeypot - siempre vacío (OBLIGATORIO)
            challengeToken: challengeToken, // Challenge Token (OBLIGATORIO)
        };

        if (codigoReferido.trim() !== '') {
            const cleanedCode = codigoReferido.trim().toLowerCase().replace(/\s+/g, '');
            try {
                const refResponse = await get(
                    `clientes/referral?code=${cleanedCode}&marcaId=${tienda.marca}`
                );
                if (!refResponse || !refResponse._id) {
                    if (onError) onError(new Error('El código de referido no es válido o no pertenece a un cliente de tu marca'));
                    return;
                }
                payload.referidoPor = refResponse._id;
            } catch (error) {
                if (onError) onError(new Error('Error al validar el código de referido'));
                return;
            }
        }

        try {
            // Usar endpoint con código si está disponible (más seguro que token JWT)
            const endpoint = code
                ? `clientes/embed/register?code=${code}`
                : 'clientes/embed/register';
            const result = await post(endpoint, payload);
            setFormData({
                nombre: '',
                apellido: '',
                email: '',
                telefonoPrefix: '+34',
                telefonoNumber: '',
                fechaCumpleanos: '',
                genero: 'PREFIERO_NO_DECIRLO',
                geographic_info: { country: '', state: '', city: '' },
                tiendaHabitual: '',
            });
            setCodigoReferido('');
            setErrors({});
            
            // Resetear challenge token después de éxito (recargar iframe)
            setChallengeToken(null);
            if (challengeFrameRef.current) {
                const currentSrc = challengeFrameRef.current.src;
                challengeFrameRef.current.src = '';
                setTimeout(() => {
                    if (challengeFrameRef.current) {
                        challengeFrameRef.current.src = currentSrc;
                        challengeFrameRef.current.style.borderColor = '#D1D5DB';
                    }
                }, 100);
            }
            
            if (onSuccess) onSuccess(result);
        } catch (error) {
            // Resetear challenge token para nuevo intento
            setChallengeToken(null);
            if (challengeFrameRef.current) {
                const currentSrc = challengeFrameRef.current.src;
                challengeFrameRef.current.src = '';
                setTimeout(() => {
                    if (challengeFrameRef.current) {
                        challengeFrameRef.current.src = currentSrc;
                        challengeFrameRef.current.style.borderColor = '#D1D5DB';
                    }
                }, 100);
            }
            if (onError) onError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="embedded-loading" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                backgroundColor: 'transparent',
                fontFamily: currentTheme.fontFamily
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        border: `4px solid ${currentTheme.primaryColor}20`,
                        borderTop: `4px solid ${currentTheme.primaryColor}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 1rem'
                    }}></div>
                    <p style={{ color: currentTheme.textColor }}>Cargando formulario...</p>
                </div>
            </div>
        );
    }

    if (!tienda) {
        return (
            <div className="embedded-error" style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#EF4444',
                fontFamily: currentTheme.fontFamily
            }}>
                <p>Error al cargar la información de la tienda</p>
            </div>
        );
    }

    const inputStyle = {
        width: '100%',
        padding: '12px 16px',
        border: `1px solid ${currentTheme.borderColor}`,
        borderRadius: currentTheme.borderRadius,
        fontSize: '16px',
        fontFamily: currentTheme.fontFamily,
        color: currentTheme.textColor,
        backgroundColor: currentTheme.backgroundColor,
        transition: 'all 0.2s ease',
        outline: 'none'
    };

    const buttonStyle = {
        width: '100%',
        padding: '12px 24px',
        backgroundColor: currentTheme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: currentTheme.borderRadius,
        fontSize: '16px',
        fontWeight: '600',
        fontFamily: currentTheme.fontFamily,
        cursor: isSubmitting ? 'not-allowed' : 'pointer',
        opacity: isSubmitting ? 0.7 : 1,
        transition: 'all 0.2s ease'
    };

    return (
        <div className={`embedded-form-container ${className}`} style={{
            fontFamily: currentTheme.fontFamily,
            color: currentTheme.textColor,
            backgroundColor: currentTheme.backgroundColor,
            maxWidth: '100%'
        }}>
            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .embedded-registration-form input:focus,
                .embedded-registration-form select:focus {
                    border-color: ${currentTheme.primaryColor} !important;
                    box-shadow: 0 0 0 3px ${currentTheme.primaryColor}20 !important;
                }
                .embedded-registration-form button:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px ${currentTheme.primaryColor}40;
                }
            `}</style>

            {showHeader && (
                <div className="embedded-header" style={{
                    textAlign: 'center',
                    marginBottom: '2rem',
                    padding: '1.5rem',
                    backgroundColor: `${currentTheme.primaryColor}10`,
                    borderRadius: currentTheme.borderRadius
                }}>
                    <h1 style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        marginBottom: '0.5rem',
                        color: currentTheme.textColor
                    }}>
                        {tienda.nombre}
                    </h1>
                    <p style={{
                        color: currentTheme.textColor,
                        opacity: 0.8,
                        marginBottom: '1rem'
                    }}>
                        {tienda.descripcion}
                    </p>
                    <div style={{
                        fontSize: '0.875rem',
                        color: currentTheme.textColor,
                        opacity: 0.7
                    }}>
                        📍 {tienda.via} {tienda.calle}, {tienda.numero}, {tienda.poblacion}
                    </div>
                </div>
            )}

            <form className="embedded-form" onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Información Personal */}
                <div style={{
                    padding: '1.5rem',
                    backgroundColor: `${currentTheme.primaryColor}05`,
                    borderRadius: currentTheme.borderRadius
                }}>
                    <h3 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        marginBottom: '1rem'
                    }}>
                        Información Personal
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '1rem'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: currentTheme.textColor
                            }}>
                                Nombre*
                            </label>
                            <input
                                type="text"
                                name="nombre"
                                value={formData.nombre}
                                onChange={handleChange}
                                style={inputStyle}
                                placeholder="Tu nombre"
                                required
                            />
                            {errors.nombre && (
                                <p style={{
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#EF4444'
                                }}>
                                    {errors.nombre}
                                </p>
                            )}
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: currentTheme.textColor
                            }}>
                                Apellido*
                            </label>
                            <input
                                type="text"
                                name="apellido"
                                value={formData.apellido}
                                onChange={handleChange}
                                style={inputStyle}
                                placeholder="Tu apellido"
                                required
                            />
                            {errors.apellido && (
                                <p style={{
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#EF4444'
                                }}>
                                    {errors.apellido}
                                </p>
                            )}
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: currentTheme.textColor
                            }}>
                                Correo electrónico*
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                style={inputStyle}
                                placeholder="tu@email.com"
                                required
                            />
                            {errors.email && (
                                <p style={{
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#EF4444'
                                }}>
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: currentTheme.textColor
                            }}>
                                Teléfono (Opcional)
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem' }}>
                                <select
                                    name="telefonoPrefix"
                                    value={formData.telefonoPrefix}
                                    onChange={handleChange}
                                    style={inputStyle}
                                >
                                    {telefonoPrefixOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    name="telefonoNumber"
                                    value={formData.telefonoNumber}
                                    onChange={handleChange}
                                    style={inputStyle}
                                    placeholder="Número de teléfono (opcional)"
                                />
                            </div>
                            {errors.telefonoNumber && (
                                <p style={{
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#EF4444'
                                }}>
                                    {errors.telefonoNumber}
                                </p>
                            )}
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: currentTheme.textColor
                            }}>
                                Fecha de Nacimiento*
                            </label>
                            <CustomDatePicker
                                name="fechaCumpleanos"
                                value={formData.fechaCumpleanos}
                                onChange={handleChange}
                                style={{
                                    ...inputStyle,
                                    width: '100%',
                                    padding: '12px 16px'
                                }}
                                placeholderText="DD/MM/AAAA"
                                required
                            />
                            {errors.fechaCumpleanos && (
                                <p style={{
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#EF4444'
                                }}>
                                    {errors.fechaCumpleanos}
                                </p>
                            )}
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: currentTheme.textColor
                            }}>
                                Género*
                            </label>
                            <select
                                name="genero"
                                value={formData.genero}
                                onChange={handleChange}
                                style={inputStyle}
                                required
                            >
                                {generoOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                            {errors.genero && (
                                <p style={{
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#EF4444'
                                }}>
                                    {errors.genero}
                                </p>
                            )}
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: currentTheme.textColor
                            }}>
                                ¿Cuál es tu tienda de consumo habitual?*
                            </label>
                            <select
                                name="tiendaHabitual"
                                value={formData.tiendaHabitual}
                                onChange={handleChange}
                                style={inputStyle}
                                required
                            >
                                <option value="">Selecciona tu tienda</option>
                                {tiendas.map((tienda) => (
                                    <option key={tienda._id} value={tienda._id}>
                                        {tienda.nombre} - {tienda.poblacion}
                                    </option>
                                ))}
                            </select>
                            {errors.tiendaHabitual && (
                                <p style={{
                                    marginTop: '0.25rem',
                                    fontSize: '0.75rem',
                                    color: '#EF4444'
                                }}>
                                    {errors.tiendaHabitual}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Código de Referido - Oculto por ahora */}
                {/* <div style={{
                    padding: '1.5rem',
                    backgroundColor: `${currentTheme.primaryColor}05`,
                    borderRadius: currentTheme.borderRadius
                }}>
                    <h3 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        marginBottom: '1rem'
                    }}>
                        ¿Tienes un código de referido?
                    </h3>
                    <input
                        type="text"
                        name="codigoReferido"
                        value={codigoReferido}
                        onChange={handleCodigoReferidoChange}
                        style={inputStyle}
                        placeholder="Ingresa tu código de referido (opcional)"
                    />
                </div> */}

                {/* Información Geográfica */}
                <div style={{
                    padding: '1.5rem',
                    backgroundColor: `${currentTheme.primaryColor}05`,
                    borderRadius: currentTheme.borderRadius
                }}>
                    <h3 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        marginBottom: '1rem'
                    }}>
                        Información Geográfica (Opcional)
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '1rem'
                    }}>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <select
                                name="geo_country"
                                value={formData.geographic_info.country}
                                onChange={handleChange}
                                style={inputStyle}
                            >
                                <option value="">Selecciona tu país</option>
                                {countryOptions.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <input
                                type="text"
                                name="geo_state"
                                value={formData.geographic_info.state}
                                onChange={handleChange}
                                style={{
                                    ...inputStyle,
                                    opacity: !formData.geographic_info.country ? 0.5 : 1,
                                    cursor: !formData.geographic_info.country ? 'not-allowed' : 'text'
                                }}
                                placeholder="Estado/Región"
                                disabled={!formData.geographic_info.country}
                            />
                        </div>

                        <div>
                            <input
                                type="text"
                                name="geo_city"
                                value={formData.geographic_info.city}
                                onChange={handleChange}
                                style={{
                                    ...inputStyle,
                                    opacity: (!formData.geographic_info.country || !formData.geographic_info.state) ? 0.5 : 1,
                                    cursor: (!formData.geographic_info.country || !formData.geographic_info.state) ? 'not-allowed' : 'text'
                                }}
                                placeholder="Ciudad"
                                disabled={!formData.geographic_info.country || !formData.geographic_info.state}
                            />
                        </div>
                    </div>
                    {errors.geographic_info && (
                        <p style={{
                            marginTop: '0.5rem',
                            fontSize: '0.75rem',
                            color: '#EF4444'
                        }}>
                            {errors.geographic_info}
                        </p>
                    )}
                </div>

                {/* Campo Honeypot (OCULTO) */}
                <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{
                        display: 'none',
                        visibility: 'hidden',
                        position: 'absolute',
                        left: '-9999px',
                    }}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                />

                {/* Iframe del Challenge Token */}
                <div style={{
                    padding: '1.5rem',
                    backgroundColor: `${currentTheme.primaryColor}05`,
                    borderRadius: currentTheme.borderRadius,
                    marginTop: '1rem',
                    marginBottom: '1rem'
                }}>
                    <div style={{
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        color: currentTheme.textColor,
                        opacity: 0.8
                    }}>
                        Verificación de seguridad
                    </div>
                    <iframe
                        ref={challengeFrameRef}
                        src={challengeUrl}
                        style={{
                            width: '100%',
                            height: '200px',
                            border: `1px solid ${challengeToken ? '#28a745' : currentTheme.borderColor}`,
                            borderRadius: currentTheme.borderRadius,
                            backgroundColor: 'transparent'
                        }}
                        sandbox="allow-scripts allow-same-origin"
                        title="Verificación de seguridad"
                    />
                    {challengeToken && (
                        <div style={{
                            marginTop: '0.5rem',
                            fontSize: '0.75rem',
                            color: '#28a745',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                        }}>
                            <span>✓</span>
                            <span>Verificación completada</span>
                        </div>
                    )}
                </div>

                {/* Botón de envío */}
                <div style={{ paddingTop: '1rem' }}>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        style={buttonStyle}
                    >
                        {isSubmitting ? 'Registrando...' : 'Unirme al Club de Clientes'}
                    </button>
                    <div style={{
                        marginTop: '1rem',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem'
                    }}>
                        <input
                            type="checkbox"
                            id="acceptTerms"
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            required
                            style={{
                                marginTop: '0.25rem',
                                cursor: 'pointer',
                                width: '18px',
                                height: '18px',
                                flexShrink: 0,
                                position: 'relative',
                                zIndex: 1,
                                pointerEvents: 'auto'
                            }}
                        />
                        <label
                            htmlFor="acceptTerms"
                            style={{
                                fontSize: '0.875rem',
                                color: currentTheme.textColor,
                                cursor: 'pointer',
                                lineHeight: '1.5'
                            }}
                        >
                            Acepto los{' '}
                            <a
                                href="/terminos"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: currentTheme.primaryColor, textDecoration: 'underline' }}
                            >
                                términos y condiciones
                            </a>
                            {' '}y la{' '}
                            <a
                                href="/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: currentTheme.primaryColor, textDecoration: 'underline' }}
                            >
                                política de privacidad
                            </a>
                            *
                        </label>
                    </div>
                    {errors.acceptTerms && (
                        <p style={{
                            marginTop: '0.5rem',
                            fontSize: '0.75rem',
                            color: '#EF4444',
                            textAlign: 'center'
                        }}>
                            {errors.acceptTerms}
                        </p>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EmbeddedRegistrationForm;
